import { Fragment } from "react"
import { useTranslation } from "react-i18next"
import type { NextPage } from "next"
import { useRouter } from "next/router"
import { Transition } from "@headlessui/react"
import clsx from "clsx"

import CustomRichText from "~/components/CustomRichText"
import LazyImage from "~/components/LazyImage"
import TailwindProseWrapper from "~/components/TailwindProseWrapper"
import { getClassesByColorScheme } from "~/utils/style/colorSchemes"

import type { AuthorCardProps } from "./AuthorCard"
import { AuthorCard } from "./AuthorCard"
import type { AuthorDetailsProps } from "./AuthorDetails"
import { AuthorDetails } from "./AuthorDetails"
import { AUTHOR_QUERY_PARAM } from "./constants"

export type AuthorDetails = Omit<AuthorDetailsProps, "isSelected"> &
  AuthorCardProps

export type ContentThemePageProps = {
  authors: AuthorDetails[]
  bannerImage: string
  title: string
  heading: prismic.RichTextField
}

export const ContentThemePage: NextPage<ContentThemePageProps> = ({
  authors,
  bannerImage,
  heading,
  title,
}) => {
  const { t } = useTranslation("contentThemePage")
  const router = useRouter()

  const selectedAuthor = authors.find(
    (author) => author.uid === router.query[AUTHOR_QUERY_PARAM],
  )

  const highlightedClass = clsx(
    "bg-primary-50 border-b-primary-50",
    "dark:bg-primary-700 dark:border-b-primary-700",
  )
  const bannerClasses = getClassesByColorScheme({})

  return (
    <div>
      <div className="relative -mx-4 flex flex-col justify-end py-1/2 sm:py-1/3 md:mx-0 md:pt-2/5 md:pb-0">
        <LazyImage
          className="absolute top-0 left-0 h-full w-full object-cover"
          src={bannerImage}
          alt=""
        />
        <header className="absolute bottom-0 left-0 z-0 flex flex-col items-start">
          <h1
            className={clsx(
              "inline-flex",
              "mt-16 py-2 px-4 md:mb-8",
              "text-3xl sm:text-4xl md:text-5xl",
              bannerClasses.bgContrast,
              bannerClasses.text,
            )}
          >
            {title}
          </h1>
        </header>
      </div>

      <TailwindProseWrapper>
        <CustomRichText field={heading} />
      </TailwindProseWrapper>

      <h2 className="text-2xl md:text-3xl">{t("authors.title")}</h2>

      <ul className="mb-8 grid grid-cols-1 md:grid-flow-row-dense md:grid-cols-3 md:gap-x-6 lg:grid-cols-4">
        {authors.map((author) => {
          const isSelectedAuthor = author === selectedAuthor

          return (
            <Fragment key={author.uid}>
              <li
                className={clsx("mt-4 duration-150 md:mt-6", {
                  [highlightedClass]: isSelectedAuthor,
                  "bg-transparent": !isSelectedAuthor,
                })}
              >
                <AuthorCard
                  name={author.name}
                  profilePic={author.profilePic}
                  uid={author.uid}
                />
              </li>

              <Transition
                show={isSelectedAuthor}
                className={clsx(
                  "origin-top duration-200 md:col-span-3 lg:col-span-4",
                  highlightedClass,
                )}
                enterFrom={clsx("opacity-0 scale-y-0")}
                enterTo={clsx("opacity-100 scale-y-100")}
                leave="hidden"
              >
                <AuthorDetails
                  id={author.id}
                  uid={author.uid}
                  name={author.name}
                  contacts={author.contacts}
                  connection={author.connection}
                  isSelected={isSelectedAuthor}
                />
              </Transition>
            </Fragment>
          )
        })}
      </ul>
    </div>
  )
}
